<template>
    <ModalTemplate
        :show="show"
        :title="$lang.get('Notification settings')"
        :wrapTitle="true"
        :description="
            $lang.get(
                'Set your email notification preferences, first decide if you want them at all. If you do you can then granularly toggle whether you want to recieve email notifications for each :organization in your network or each favorite.',
                {
                    organization: $getBaseModelTranslation({
                        modelName: 'organization',
                    }),
                }
            )
        "
        @close="close"
        width="max-w-2xl w-full"
    >
        <ReactiveHeightTransition>
            <div class="flex flex-col gap-y-4 max-h-[50vh] p-1" v-if="!loading">
                <div class="flex flex-col gap-y-2">
                    <p
                        v-text="$lang.get('Recieve email notifications')"
                        class="text-header text-xl"
                    />
                    <div class="flex gap-x-2">
                        <ToggleSwitch
                            :checked="!!recieveEmails"
                            @clicked="onRecieveEmailsChecked"
                            :loading="recieveEmailsLoading"
                            :class="
                                recieveEmails ? 'bg-blue-600' : 'bg-gray-200'
                            "
                            height="h-6"
                            width="w-12"
                        />
                        <p
                            v-text="
                                recieveEmails
                                    ? $lang.get(
                                          'You will recieve email notifications'
                                      )
                                    : $lang.get(
                                          'You will not recieve email notifications'
                                      )
                            "
                        />
                    </div>
                </div>
                <div class="mt-5">
                    <p
                        v-text="$lang.get('Recieve email notifications for:')"
                        class="text-header text-xl mb-1"
                    />
                    <Tabs v-if="recieveEmails" :tabs="groups">
                        <template
                            v-for="group in groups"
                            v-slot:[group.id]
                            :tab="group"
                        >
                            <div
                                v-if="group.models.length > 0"
                                class="overflow-y-auto max-h-[30svh] space-y-3 px-2"
                            >
                                <ModelPreview
                                    v-for="model in group.models"
                                    :key="model.id"
                                    :imageSrc="model.imageUrl"
                                    :modelClass="model.modelClass"
                                    imageSize="size-16"
                                    imageRounded="rounded-md rounded-r-none"
                                    imageBorder=""
                                    componentPadding=""
                                    contentPadding="py-1"
                                    componentBorder="border border-gray-100"
                                    :modelName="model.name"
                                >
                                    <template v-slot:content>
                                        <div class="flex gap-x-2">
                                            <ToggleSwitch
                                                :checked="!!model.notify"
                                                @clicked="updateModel(model)"
                                                :loading="
                                                    recieveEmailsLoadingOrganizationId ===
                                                    model.id
                                                "
                                                :class="
                                                    model.notify
                                                        ? 'bg-blue-600'
                                                        : 'bg-gray-200'
                                                "
                                                class="relative inline-flex h-6 w-11 items-center rounded-full"
                                            >
                                                <span class="sr-only"
                                                    >Enable notification for
                                                    {{ model.title }}</span
                                                >
                                                <span
                                                    :class="
                                                        model.notify
                                                            ? 'translate-x-6'
                                                            : 'translate-x-1'
                                                    "
                                                    class="inline-block h-4 w-4 transform rounded-full bg-white transition"
                                                />
                                            </ToggleSwitch>
                                            <p
                                                v-text="
                                                    model.notify
                                                        ? $lang.get(
                                                              'You will recieve email notifications'
                                                          )
                                                        : $lang.get(
                                                              'You will not recieve email notifications'
                                                          )
                                                "
                                            />
                                        </div>
                                    </template>
                                </ModelPreview>
                            </div>
                            <p v-else v-text="group.no_models_text" />
                        </template>
                    </Tabs>
                </div>
            </div>
            <div
                v-else
                class="flex flex-col justify-center items-center p-5 gap-y-2"
            >
                <PhCircleNotch class="size-8 animate-spin text-primary" />
                <p
                    class="animate-pulse"
                    v-text="$lang.get('Fetching notification settings')"
                />
            </div>
        </ReactiveHeightTransition>
    </ModalTemplate>
</template>

<script>
import { PhCircleNotch, PhQuestion } from '@phosphor-icons/vue'
import ModelPreview from '@/Components/ModelComponents/ModelPreview.vue'
import ToggleSwitch from '@/Components/Elements/ToggleSwitch.vue'
import ModalTemplate from '@/Components/Templates/ModalTemplate.vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import Checkbox from '@/Components/FormElements/Checkbox.vue'
import TextTooltip from '@/Components/Elements/TextTooltip.vue'
import Tabs from '@/Components/Utilities/Tabs.vue'

import { minimumLoadingTime } from '@/Tools/utilities'

export default {
    name: 'NotificationSettingModal',

    components: {
        ModalTemplate,
        ReactiveHeightTransition,
        PhCircleNotch,
        PhQuestion,
        Checkbox,
        ToggleSwitch,
        TextTooltip,
        Tabs,
        ModelPreview,
    },

    expose: ['open'],

    data() {
        return {
            show: false,
            currentStepID: 'favorites',
            favorites: [],
            affiliations: [],
            recieveEmails: false,
            loading: true,
            recieveEmailsLoading: false,
            recieveEmailsLoadingOrganizationId: null,
            recieveEmailsLoadingFavoriteId: null,
            resolveCallback: null,
        }
    },

    watch: {
        show(value) {
            if (value) {
                this.fetchSettings()
            } else {
                this.affiliations = []
                this.favorites = []

                this.loading = true
            }
        },
    },

    computed: {
        groups() {
            return [
                {
                    id: 'favorites',
                    title:
                        this.$lang.get('Favorites') +
                        '(' +
                        this.favorites.length +
                        ')',
                    no_models_text: this.$lang.get(
                        'You have no favorites that will send you emails'
                    ),
                    description: this.$lang.get(
                        'You will receive notifications if there are any updates to a favorite or a descendant to a favorite.'
                    ),
                    models: this.favorites.map((v) => ({
                        id: v.id,
                        name: v.referenceable.name,
                        imageUrl: v.referenceable?.first_image?.preview_url,
                        modelClass: v.referenceable?.modelClass,
                        notify: v.number == 1 ? true : false,
                        type: 'favorite',
                    })),
                },
                {
                    id: 'network',
                    title:
                        this.$lang.get('Network') +
                        '(' +
                        this.affiliations.length +
                        ')',
                    no_models_text: this.$lang.get(
                        'You have no relationships in your network that will send you emails'
                    ),
                    description: this.$lang.get(
                        'You will receive notifications if there are any updates to a relationship or a descendant to a relationship.'
                    ),
                    models: this.affiliations.map((r) => ({
                        id: r.pivot.id,
                        name: r.name,
                        imageUrl: r.first_image?.preview_url,
                        modelClass: r.modelClass,
                        notify: r.pivot.notify_email_new_children,
                        type: 'affiliation',
                    })),
                },
            ]
        },
    },

    methods: {
        open() {
            this.show = true
            return new Promise((resolve) => {
                this.resolveCallback = resolve
            })
        },

        close() {
            this.show = false
            this.resolveCallback(false)
        },

        setStep(stepID) {
            this.currentStepID = stepID
        },

        updateModel(model) {
            if (!this.recieveEmailsLoadingOrganizationId) {
                this.recieveEmailsLoadingOrganizationId = model.id
                axios
                    .patch(
                        route('api.profile.toggle-receiving-emails-explicit'),
                        {
                            sources: [
                                {
                                    type: model.type,
                                    id: model.id,
                                    notify: !model.notify,
                                },
                            ],
                        }
                    )
                    .then(({ data }) => {
                        model.notify = data.notify
                    })
                    .finally(() => {
                        this.recieveEmailsLoadingOrganizationId = null
                    })
            }
        },

        onRecieveEmailsChecked() {
            this.recieveEmailsLoading = true

            axios
                .patch(route('api.profile.toggle-receiving-emails'))
                .then((response) => {
                    this.recieveEmails = response.data
                })
                .finally(() => {
                    this.recieveEmailsLoading = false
                })
        },

        fetchSettings() {
            const handleResponse = minimumLoadingTime((response) => {
                this.favorites = response.data.favorites
                this.affiliations = response.data.affiliations
                this.recieveEmails = response.data.receive_emails
                this.loading = false
            }, 500)

            axios.get(route('api.profile.notifications')).then((response) => {
                handleResponse(response)
            })
        },
    },
}
</script>
